import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BiSave, BiKey, BiLock, BiShow, BiHide, BiImageAdd } from 'react-icons/bi';
import { API_BASE_URL } from '../utils/urls.js';
import axiosClient from '../utils/apiClient.js';
import { toast } from 'react-toastify';

const UserForm = ({ isNew = false }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone_number: '',
        role_id: '',
        region: '',
        profile_image: '' // State to store the profile image URL
    });
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_new_password: ''
    });
    const [adminPasswordData, setAdminPasswordData] = useState({
        new_password: '',
        confirm_new_password: ''
    });
    const [roles, setRoles] = useState([]);
    const [message, setMessage] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isUploading, setIsUploading] = useState(false); // State for upload progress
    const [isPasswordChanging, setIsPasswordChanging] = useState(false);
    const [isAdminPasswordChanging, setIsAdminPasswordChanging] = useState(false);
    const [isPasswordFormOpen, setIsPasswordFormOpen] = useState(false);
    const [isAdminPasswordFormOpen, setIsAdminPasswordFormOpen] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [thisRole, setThisRole] = useState(null);
    const [selectedRoleName, setSelectedRoleName] = useState('');
    const user = JSON.parse(localStorage.getItem('user'));

    // Mock role list from the original code
    const rolesListForAdmin = [
        { "id": 1, "role_name": "admin" },
        { "id": 2, "role_name": "case_manager" },
        { "id": 3, "role_name": "field_agent" },
        { "id": 5, "role_name": "servers" }
    ];

        useEffect(() => {
            const fetchCaseLimitStatus = async () => {
                try {
                    const { data } = await axiosClient.get('/subs/case-limit');
                    console.log("case limit data", data)
    
                        if (data.subscriptionExpired) {
                            alert("Your subscription has expired. Please renew to create or update teams.");
                            return navigate('/cases');
                        }
    
                    if (!data.canAddUsers) {
                        if(isNew){
                            alert("Your plan user limit is reached.\nDelete any user to add a new one.");
                            setTimeout(() => {
                                navigate('/teams');
                            }, 100); // Slight delay ensures alert renders
                        } 
                    }
    
    
                } catch (err) {
                    console.error(err);
                }
            };
    
            fetchCaseLimitStatus();
        }, [navigate]);

    useEffect(() => {
        const fetchUserDataAndRoles = async () => {
            try {
                const token = localStorage.getItem('token');
                let userData = { region: '', profile_image: '' };

                // Step 1: Fetch user data only if we are editing and an ID is available
                if (!isNew && id) {
                    const userResponse = await axios.get(`${API_BASE_URL}/teams/${id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    userData = userResponse.data;

                    const roleData = rolesListForAdmin.find(e => e.role_name === userData.role_name);

                    if (roleData) {
                        setThisRole(roleData.id);
                        setSelectedRoleName(roleData.role_name);
                    }
                }

                setRoles(rolesListForAdmin);

                // Step 3: Set form data after fetching is complete
                setFormData(prevState => ({
                    ...prevState,
                    ...userData,
                    role_id: userData.role_id ? String(userData.role_id) : '',
                    profile_image: userData.profile_image || ''
                }));

            } catch (error) {
                setMessage('Error fetching data. Please check your network and permissions.');
                console.error('Error fetching data:', error);
            }
        };

        if (!isNew && !user) {
            navigate('/login');
            return;
        }

        fetchUserDataAndRoles();
    }, [id, isNew, user?.roleName, navigate]);

    // Role, Region, and Standard field change handler
    const handleChange = (e) => {
        const { name, value } = e.target;

        if (name === 'role_id') {
            const newRole = roles.find(r => String(r.id) === value);
            const newRoleName = newRole ? newRole.role_name : '';

            setThisRole(Number(value));
            setSelectedRoleName(newRoleName);

            setFormData(prevData => ({
                ...prevData,
                [name]: value,
                // If the new role is NOT 'servers', clear the region field
                ...(newRoleName !== 'servers' && { region: '' })
            }));
            return;
        }

        setFormData({ ...formData, [name]: value });
    };

    // ✨ UPDATED: Handler for Profile Image Upload
    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadData = new FormData();
        uploadData.append('image', file);

        setIsUploading(true);
        setMessage(null);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_BASE_URL}/teams/upload-image`, uploadData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Extract the relative URL from the response
            const relativeUrl = response.data.url;

            // ✨ FIX: Prepend API_BASE_URL to the relative path
            let finalImageUrl = relativeUrl;
            if (relativeUrl && relativeUrl.startsWith('/uploads')) {
                // Remove trailing slash from API_BASE_URL if present, before concatenating
                const base = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
                finalImageUrl = base + relativeUrl;
            }

            // Update formData with the new absolute profile_image URL
            setFormData(prevData => ({
                ...prevData,
                profile_image: finalImageUrl
            }));

           // setMessage('Image uploaded successfully. Click "Save Changes" to finalize.');

        } catch (error) {
            toast.error('Failed to upload image. Please try again.');
            console.error('Image upload error:', error.response?.data || error);
        } finally {
            setIsUploading(false);
            e.target.value = null;
        }
    };


    const handlePasswordChange = (e) => {
        setPasswordData({ ...passwordData, [e.target.name]: e.target.value });
    };

    const handleAdminPasswordChange = (e) => {
        setAdminPasswordData({ ...adminPasswordData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        setMessage(null);

        let dataToSubmit = { ...formData };

        if (selectedRoleName !== 'servers') {
            delete dataToSubmit.region;
        }

        // IMPORTANT: If you want to store ONLY the relative path in the database 
        // (which is generally better practice), you need to convert it back here.
        // Assuming your backend stores the full URL or the relative path:

        /* // OPTIONAL: Convert back to relative path if backend expects it
        if (dataToSubmit.profile_image && dataToSubmit.profile_image.startsWith(API_BASE_URL)) {
             dataToSubmit.profile_image = dataToSubmit.profile_image.replace(API_BASE_URL, '');
        } 
        // For simplicity and matching the server upload response, we'll keep the value from state (the absolute path) 
        // and assume your backend can handle cleaning it up or saving the full path.
        */

        try {
            const token = localStorage.getItem('token');
            if (isNew) {
                await axios.post(API_BASE_URL + '/teams/create', dataToSubmit, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                navigate('/teams');
            } else {
                await axios.put(`${API_BASE_URL}/teams/${id}`, dataToSubmit, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMessage('Profile updated successfully.');
            }
        } catch (error) {
            setMessage('Failed : ' + (error.response?.data?.message || 'Error saving user data.'));
            toast.error('Failed to save user data.');
            console.error('Error saving user data:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setIsPasswordChanging(true);
        setMessage(null);
        if (passwordData.new_password !== passwordData.confirm_new_password) {
            setMessage('New passwords do not match.');
            setIsPasswordChanging(false);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            if (!user) {
                throw new Error("User not authenticated.");
            }
            await axios.put(`${API_BASE_URL}/users/change-password/${user.id}`, passwordData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Password updated successfully.');
            setPasswordData({ current_password: '', new_password: '', confirm_new_password: '' });
            setIsPasswordFormOpen(false);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to change password.');
            console.error('Error changing password:', error);
        } finally {
            setIsPasswordChanging(false);
        }
    };

    const handleAdminPasswordSubmit = async (e) => {
        e.preventDefault();
        setIsAdminPasswordChanging(true);
        setMessage(null);
        if (adminPasswordData.new_password !== adminPasswordData.confirm_new_password) {
            setMessage('New passwords do not match.');
            setIsAdminPasswordChanging(false);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_BASE_URL}/users/reset-password/${id}`, { new_password: adminPasswordData.new_password }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setMessage('Password updated successfully by admin.');
            setAdminPasswordData({ new_password: '', confirm_new_password: '' });
            setIsAdminPasswordFormOpen(false);
        } catch (error) {
            setMessage(error.response?.data?.message || 'Failed to reset password.');
            console.error('Error resetting password:', error);
        } finally {
            setIsAdminPasswordChanging(false);
        }
    };

    const isSelfEdit = id && user && parseInt(id) === user.id;

    return (
        <div className="container mx-auto p-1 md:p-4">
            <h1 className="text-3xl font-bold text-gray-800 mb-6">
                {isNew ? 'Create New Team Member' : 'Edit Team Member Profile'}
            </h1>
            {message && (
                <div className={`p-3 rounded-md mb-4 ${message.includes('successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {message}
                </div>
            )}

            <div className="bg-white shadow-lg rounded-lg p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Team Member Details</h2>
                <form onSubmit={handleSubmit}>

                    {/* Profile Image Section */}
                    <div className="mb-6 flex items-center space-x-6">
                        <div className="flex-shrink-0">
                            <img
                                className="h-20 w-20 object-cover rounded-full border-2 border-gray-200"
                                // Use the full absolute URL for display
                                src={formData.profile_image || 'https://via.placeholder.com/80?text=No+Image'}
                                alt="Profile"
                            />
                        </div>
                        <div>
                            <label htmlFor="file-upload" className="relative cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500 flex items-center">
                                <BiImageAdd className="mr-2 h-5 w-5" />
                                {isUploading ? 'Uploading...' : 'Change Photo'}
                            </label>
                            <input
                                id="file-upload"
                                name="image"
                                type="file"
                                className="sr-only"
                                onChange={handleImageUpload}
                                disabled={isUploading}
                                accept="image/*"
                            />
                            {formData.profile_image && (
                                <p className="mt-2 text-xs text-gray-500 break-all">Image set. Click Save Changes.</p>
                            )}
                        </div>
                    </div>
                    {/* End Profile Image Section */}

                    <div className={`grid gap-4 mb-4 ${selectedRoleName === 'servers' ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
                        {/* Basic Fields */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700">First Name</label>
                            <input type="text" name="first_name" value={formData.first_name} onChange={handleChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Last Name</label>
                            <input type="text" name="last_name" value={formData.last_name} onChange={handleChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Phone Number</label>
                            <input type="text" name="phone_number" value={formData.phone_number} onChange={handleChange} className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                        </div>

                        {/* Role Selection (Admin Only) */}
                        {user?.roleName === 'admin' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Role</label>
                                <select
                                    name="role_id"
                                    value={formData.role_id || thisRole || ''}
                                    onChange={(e) => {
                                        const { name, value } = e.target;
                                        setThisRole(value);  // update role_id in state
                                        handleChange(e);    // handle additional logic
                                    }}
                                    required
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
                                >
                                    <option value="">Select a role</option>
                                    {roles.map((role) => (
                                        <option key={role.id} value={String(role.id)}>
                                            {role.role_name
                                                .replace(/_/g, " ")
                                                .split(" ")
                                                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                                .join(" ")}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}

                        {/* Dynamic Region Field for 'servers' role */}
                        {selectedRoleName === 'servers' && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Region (Zip Codes, comma separated)</label>
                                <input
                                    type="text"
                                    name="region"
                                    value={formData.region}
                                    onChange={handleChange}
                                    placeholder="e.g., 90210, 10001, 75001"
                                    required={selectedRoleName === 'servers'}
                                    className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2"
                                />
                            </div>
                        )}

                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-700">Password</label>
                            <input
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                autoComplete="new-password"
                                className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2 pr-10"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-[35px] text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                                {showPassword ? <BiHide size={20} /> : <BiShow size={20} />}
                            </button>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-4">
                        <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={isSaving || isUploading} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                            <BiSave className="mr-2" /> {isSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Admin password reset form */}
            {user?.roleName === 'admin' && !isNew && (
                <div className="bg-white shadow-lg rounded-lg p-6 mt-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Admin Password Reset</h2>
                        <button onClick={() => setIsAdminPasswordFormOpen(!isAdminPasswordFormOpen)} className="text-blue-600 hover:text-blue-800 transition">
                            {isAdminPasswordFormOpen ? 'Close' : 'Reset Password'}
                        </button>
                    </div>
                    {isAdminPasswordFormOpen && (
                        <form onSubmit={handleAdminPasswordSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">New Password</label>
                                    <input type="password" name="new_password" value={adminPasswordData.new_password} onChange={handleAdminPasswordChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                                    <input type="password" name="confirm_new_password" value={adminPasswordData.confirm_new_password} onChange={handleAdminPasswordChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                                </div>
                            </div>
                            <div className="flex justify-end mt-4">
                                <button type="submit" disabled={isAdminPasswordChanging} className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50">
                                    <BiLock className="mr-2" /> {isAdminPasswordChanging ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}

            {/* Password change form for the user themselves */}
            {isSelfEdit && (
                <div className="bg-white shadow-lg rounded-lg p-6 mt-6">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Change Password</h2>
                        <button onClick={() => setIsPasswordFormOpen(!isPasswordFormOpen)} className="text-blue-600 hover:text-blue-800 transition">
                            {isPasswordFormOpen ? 'Close' : 'Change Password'}
                        </button>
                    </div>
                    {isPasswordFormOpen && (
                        <form onSubmit={handlePasswordSubmit}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Current Password</label>
                                    <input type="password" name="current_password" value={passwordData.current_password} onChange={handlePasswordChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">New Password</label>
                                    <input type="password" name="new_password" value={passwordData.new_password} onChange={handlePasswordChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                                    <input type="password" name="confirm_new_password" value={passwordData.confirm_new_password} onChange={handlePasswordChange} required className="shadow-sm bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-none focus:ring-4 focus:ring-indigo-200 block w-full p-1.5 mt-2" />
                                </div>
                            </div>
                            <div className="flex justify-end mt-4">
                                <button type="submit" disabled={isPasswordChanging} className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                                    <BiKey className="mr-2" /> {isPasswordChanging ? 'Updating...' : 'Update Password'}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            )}
        </div>
    );
};

export default UserForm;